import React from "react";
import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import "./editor.css";
import "./lexical-editor.css";

// Lexical imports
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"; // Added import
import { 
  $getRoot, 
  $createParagraphNode, 
  $createTextNode, 
  $getSelection, 
  $isRangeSelection,
  createCommand,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  KEY_MODIFIER_COMMAND
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode } from "@lexical/rich-text";
import { $createQuoteNode } from "@lexical/rich-text";
import { 
  $convertFromMarkdownString, 
  $convertToMarkdownString,
  TRANSFORMERS
} from "@lexical/markdown";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND
} from "@lexical/list";
import {
  TOGGLE_LINK_COMMAND,
  $createLinkNode,
  $isLinkNode
} from "@lexical/link";

// Import icons from react-icons (Font Awesome) and Heroicons
import { 
  FaHeading, 
  FaParagraph, 
  FaBold, 
  FaItalic, 
  FaCode, 
  FaListUl, 
  FaListOl, 
  FaQuoteLeft, 
  FaSave, 
  FaSpinner,
  FaKeyboard,
  FaLink
} from "react-icons/fa";
import { CheckIcon } from '@heroicons/react/solid';
import InfoTooltip from "../../UI/InfoTooltip";

// ToolbarButton component
const ToolbarButton = ({ active, disabled, onClick, icon, title }) => {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`p-2 rounded-md transition-colors ${
        active 
          ? "bg-primary-100 text-primary-600" 
          : "text-primary-600 hover:bg-neutral-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      title={title}
      aria-label={title}
    >
      {icon}
    </button>
  );
};

// Heading toolbar plugin
function HeadingToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [activeHeading, setActiveHeading] = useState(null);

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const element = anchorNode.getKey() === 'root' 
          ? anchorNode 
          : anchorNode.getTopLevelElementOrThrow();
        
        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);
        
        if (elementDOM) {
          if (element.getType() === 'heading') {
            const tag = element.getTag();
            setActiveHeading(tag);
          } else {
            setActiveHeading(null);
          }
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatHeading = (headingSize) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (headingSize === 'p') {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      }
    });
  };

  return (
    <div className="flex space-x-1">
      <ToolbarButton
        active={activeHeading === 'p' || activeHeading === null}
        onClick={() => formatHeading('p')}
        icon={<FaParagraph className="h-4 w-4" />}
        title="Normal Text"
      />
      <ToolbarButton
        active={activeHeading === 'h1'}
        onClick={() => formatHeading('h1')}
        icon={<div className="flex items-center"><FaHeading className="h-4 w-4" /><span className="text-xs ml-1">1</span></div>}
        title="Heading 1"
      />
      <ToolbarButton
        active={activeHeading === 'h2'}
        onClick={() => formatHeading('h2')}
        icon={<div className="flex items-center"><FaHeading className="h-4 w-4" /><span className="text-xs ml-1">2</span></div>}
        title="Heading 2"
      />
      <ToolbarButton
        active={activeHeading === 'h3'}
        onClick={() => formatHeading('h3')}
        icon={<div className="flex items-center"><FaHeading className="h-4 w-4" /><span className="text-xs ml-1">3</span></div>}
        title="Heading 3"
      />
    </div>
  );
}

// Format toolbar plugin
function FormatToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));
        setIsCode(selection.hasFormat('code'));
        setIsUnderline(selection.hasFormat('underline'));
        setIsStrikethrough(selection.hasFormat('strikethrough'));
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatCode = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
  };

  const formatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  };

  const formatStrikethrough = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  };

  return (
    <div className="flex space-x-1">
      <ToolbarButton
        active={isBold}
        onClick={formatBold}
        icon={<FaBold className="h-4 w-4" />}
        title="Bold"
      />
      <ToolbarButton
        active={isItalic}
        onClick={formatItalic}
        icon={<FaItalic className="h-4 w-4" />}
        title="Italic"
      />
      <ToolbarButton
        active={isUnderline}
        onClick={formatUnderline}
        icon={<span className="underline text-xs font-bold">U</span>}
        title="Underline"
      />
      <ToolbarButton
        active={isStrikethrough}
        onClick={formatStrikethrough}
        icon={<span className="line-through text-xs font-bold">S</span>}
        title="Strikethrough"
      />
      <ToolbarButton
        active={isCode}
        onClick={formatCode}
        icon={<FaCode className="h-4 w-4" />}
        title="Inline Code"
      />
    </div>
  );
}

// List toolbar plugin
function ListToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBulletList, setIsBulletList] = useState(false);
  const [isNumberedList, setIsNumberedList] = useState(false);

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        if (anchorNode) {
          const element = anchorNode.getKey() === 'root' 
            ? anchorNode 
            : anchorNode.getTopLevelElementOrThrow();
          
          // Check if the element is a list item by traversing up the tree
          let parent = element.getParent();
          let isList = false;
          let listType = null;
          
          if (parent) {
            const parentList = parent.getParent();
            if (parentList && parentList.getType() === 'list') {
              isList = true;
              listType = parentList.getListType();
            }
          }
          
          setIsBulletList(isList && listType === 'bullet');
          setIsNumberedList(isList && listType === 'number');
        } else {
          setIsBulletList(false);
          setIsNumberedList(false);
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBulletList = () => {
    if (isBulletList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (isNumberedList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  return (
    <div className="flex space-x-1">
      <ToolbarButton
        active={isBulletList}
        onClick={formatBulletList}
        icon={<FaListUl className="h-4 w-4" />}
        title="Bullet List"
      />
      <ToolbarButton
        active={isNumberedList}
        onClick={formatNumberedList}
        icon={<FaListOl className="h-4 w-4" />}
        title="Numbered List"
      />
    </div>
  );
}

// Quote toolbar plugin
function QuoteToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isQuote, setIsQuote] = useState(false);

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        if (anchorNode) {
          const element = anchorNode.getKey() === 'root' 
            ? anchorNode 
            : anchorNode.getTopLevelElementOrThrow();
          
          setIsQuote(element.getType() === 'quote');
        } else {
          setIsQuote(false);
        }
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatQuote = () => {
    if (isQuote) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    } else {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  return (
    <ToolbarButton
      active={isQuote}
      onClick={formatQuote}
      icon={<FaQuoteLeft className="h-4 w-4" />}
      title="Quote"
    />
  );
}

// Link toolbar plugin
function LinkToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isLink, setIsLink] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkSelection, setLinkSelection] = useState(null);

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Check if the current selection contains a link node
        const nodes = selection.getNodes();
        let linkNodeFound = false;
        
        for (const node of nodes) {
          if ($isLinkNode(node) || (node.getParent() && $isLinkNode(node.getParent()))) {
            linkNodeFound = true;
            break;
          }
        }
        
        setIsLink(linkNodeFound);
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const toggleLink = useCallback(() => {
    if (isLink) {
      // If it's already a link, remove it
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      // Otherwise, store the selection and show the link modal
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setLinkSelection(selection.clone());
          setShowLinkModal(true);
        }
      });
    }
  }, [editor, isLink]);

  const handleSubmitLink = useCallback(() => {
    // Only apply if we have a selection and a URL
    if (linkSelection && linkUrl) {
      editor.update(() => {
        // Apply the link to the saved selection
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl === '' ? null : linkUrl);
        
        // Reset state
        setShowLinkModal(false);
        setLinkUrl('https://');
        setLinkSelection(null);
      });
    }
  }, [editor, linkUrl, linkSelection]);

  const handleCancel = useCallback(() => {
    setShowLinkModal(false);
    setLinkUrl('https://');
    setLinkSelection(null);
  }, []);

  return (
    <>
      <ToolbarButton
        active={isLink}
        onClick={toggleLink}
        icon={<FaLink className="h-4 w-4" />}
        title={isLink ? "Remove Link" : "Insert Link"}
      />
      
      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-500 bg-opacity-75" onClick={handleCancel}></div>
            </div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-neutral-900 mb-4">Insert Link</h3>
                    <div className="mt-2">
                      <input
                        type="url"
                        className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="Enter a valid URL (e.g., https://example.com)"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-neutral-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleSubmitLink}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-neutral-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// StatusBar component to show saving status
function StatusBar({ status }) {
  const shortcutText = navigator.platform.includes('Mac') ? '⌘S' : 'Ctrl+S';
  
  return (
    <div className="editor-statusbar flex items-center justify-between border-t border-neutral-100 py-1.5 px-3 text-xs text-neutral-500">
      <div className="flex items-center">
        <span className="hidden sm:inline">Press <kbd className="px-1.5 py-0.5 mx-1 bg-neutral-100 border border-neutral-200 rounded font-mono text-primary-700">{shortcutText}</kbd> to save your changes</span>
        <span className="sm:hidden">Save with <kbd className="px-1 py-0.5 mx-0.5 bg-neutral-100 border border-neutral-200 rounded font-mono text-primary-700">{shortcutText}</kbd></span>
      </div>
      <div className="flex items-center">
        {status === 'saving' ? (
          <div className="flex items-center text-secondary-500">
            <FaSpinner className="h-3 w-3 mr-1 animate-spin" />
            <span>Saving...</span>
          </div>
        ) : status === 'saved' ? (
          <div className="flex items-center text-support-green-500">
            <FaSave className="h-3.5 w-3.5 mr-1" />
            <span>Saved</span>
          </div>
        ) : (
          <div className="flex items-center">
            <FaSave className="h-3 w-3 mr-1" />
            <span>Ready</span>
          </div>
        )}
      </div>
    </div>
  );
}

// SaveButton component with keyboard shortcut support
function SaveButton({ onSave, isSaving, saveStatus }) {
  const shortcutText = navigator.platform.includes('Mac') ? '⌘S' : 'Ctrl+S';

  const baseClasses = "inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform active:scale-95";
  const statusClasses = saveStatus === 'saved'
    ? "bg-support-green-500 hover:bg-support-green-600 focus:ring-support-green-500"
    : "bg-secondary-500 hover:bg-secondary-600 focus:ring-secondary-500";
  const disabledClasses = "disabled:opacity-50 disabled:cursor-not-allowed";
  const finalClassName = `${baseClasses} ${statusClasses} ${disabledClasses}`;

  return (
    <div className="flex items-center mr-1">
      <button
        onClick={onSave}
        disabled={isSaving}
        className={finalClassName}
        title={`Save Notes (${shortcutText})`}
        aria-label={`Save Notes (${shortcutText})`}
      >
        {isSaving ? (
          <>
            <FaSpinner className="h-4 w-4 mr-1.5 animate-spin" />
            <span className="hidden xs:inline">Saving...</span>
            <span className="xs:hidden">...</span>
          </>
        ) : saveStatus === 'saved' ? (
          <>
            <FaSave className="h-4 w-4 mr-1.5 text-white" />
            <span className="hidden xs:inline">Saved!</span>
            <span className="xs:hidden">Done</span>
          </>
        ) : (
          <>
            <FaSave className="h-4 w-4 mr-1.5" />
            <span className="hidden xs:inline">Save</span>
          </>
        )}
      </button>
    </div>
  );
}

// Toolbar container
function Toolbar({ onSave, isSaving, saveStatus }) {
  return (
    <div className="editor-toolbar flex flex-wrap items-center justify-between border border-neutral-100 bg-neutral-100 rounded-t-md">
      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
        <div className="flex items-center">
          <HeadingToolbarPlugin />
        </div>
        <div className="hidden xs:block h-6 w-px bg-neutral-100"></div>
        <div className="flex items-center">
          <FormatToolbarPlugin />
        </div>
        <div className="hidden xs:block h-6 w-px bg-neutral-100"></div>
        <div className="flex items-center">
          <ListToolbarPlugin />
        </div>
        <div className="hidden xs:block h-6 w-px bg-neutral-100"></div>
        <div className="flex items-center">
          <QuoteToolbarPlugin />
        </div>
        <div className="hidden xs:block h-6 w-px bg-neutral-100"></div>
        <div className="flex items-center">
          <LinkToolbarPlugin />
        </div>
      </div>
      <div className="flex items-center ml-1 sm:ml-2">
        <SaveButton 
          onSave={onSave} 
          isSaving={isSaving} 
          saveStatus={saveStatus}
        />
      </div>
    </div>
  );
}

// Register commands plugin to ensure proper command registration
function RegisterCommandsPlugin() {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    // Register any additional custom commands if needed
    
    // Allow format text commands to work properly
    editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      (format) => {
        // This is handled by Lexical's core, just need to register the command
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
    
    // Allow format element commands to work properly
    editor.registerCommand(
      FORMAT_ELEMENT_COMMAND,
      (format) => {
        // This is handled by Lexical's core, just need to register the command
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
    
    // Allow list commands to work properly
    editor.registerCommand(
      INSERT_UNORDERED_LIST_COMMAND,
      () => {
        // This is handled by ListPlugin, just need to register the command
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
    
    editor.registerCommand(
      INSERT_ORDERED_LIST_COMMAND,
      () => {
        // This is handled by ListPlugin, just need to register the command
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
    
    editor.registerCommand(
      REMOVE_LIST_COMMAND,
      () => {
        // This is handled by ListPlugin, just need to register the command
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
  }, [editor]);
  
  return null;
}

// Plugin to capture editor instance
function EditorCapturePlugin({ captureEditor }) {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    captureEditor(editor);
  }, [editor, captureEditor]);
  
  return null;
}

// Initialize Editor with markdown content
function InitialContentPlugin({ initialContent }) {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      
      if (initialContent && initialContent.trim() !== "") {
        // Convert markdown to Lexical nodes
        $convertFromMarkdownString(initialContent, TRANSFORMERS);
      } else {
        // Set empty state if no notes
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(""));
        root.append(paragraph);
      }
    });
  }, [editor, initialContent]);
  
  return null;
}

// Editor component that handles saving content
function EditorSavePlugin({ onChange, episodeId, onSaveStateChange }) {
  const [editor] = useLexicalComposerContext();
  
  // Get current content as markdown - useful for manual save operations
  const getCurrentContent = useCallback(() => {
    let content = "";
    editor.getEditorState().read(() => {
      content = $convertToMarkdownString(TRANSFORMERS);
    });
    return content;
  }, [editor]);
  
  // Auto-save functionality has been removed
  // The component now only provides getCurrentContent for manual save operations
  
  return null;
}

// Manual save handler plugin
function SaveOnDemandPlugin({ saveNow, onChange, episodeId, onSaveStateChange }) {
  const [editor] = useLexicalComposerContext();
  const [previousSaveNow, setPreviousSaveNow] = useState(false);
  
  // Track saveNow changes to prevent duplicate saves
  useEffect(() => {
    if (saveNow && !previousSaveNow) {
      console.log("SaveOnDemandPlugin: Detected saveNow change, triggering save");
      onSaveStateChange(true);
      
      editor.getEditorState().read(() => {
        const markdown = $convertToMarkdownString(TRANSFORMERS);
        
        onChange({
          episode_id: episodeId,
          data: markdown,
        });
        
        // Let parent know save completed
        setTimeout(() => {
          onSaveStateChange(false);
        }, 300);
      });
    }
    
    setPreviousSaveNow(saveNow);
  }, [saveNow, previousSaveNow, editor, onChange, episodeId, onSaveStateChange]);
  
  return null;
}

// SaveKeyboardShortcutPlugin for keyboard shortcut support
function SaveKeyboardShortcutPlugin({ onSave }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Add event listener for Ctrl+S / Cmd+S to save
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        // Prevent the browser's save dialog
        event.preventDefault();
        onSave();
      }
    };

    // Add the event listener to the window
    window.addEventListener('keydown', handleKeyDown);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onSave]);

  return null;
}

export const Editor = (props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('ready'); // 'ready', 'saving', 'saved'
  
  // Theme configuration for Lexical aligned with PodScribe design system
  const theme = {
    root: "prose min-w-full text-primary-800 outline-none",
    paragraph: "my-2",
    heading: {
      h1: "text-lg font-semibold text-primary-800 mt-5 mb-3",
      h2: "text-base font-semibold text-primary-700 mt-4 mb-2",
      h3: "text-sm font-semibold text-primary-600 mt-3 mb-2",
      h4: "text-sm font-medium text-primary-400 mt-3 mb-2",
    },
    list: {
      ul: "list-disc ml-6 my-3",
      ol: "list-decimal ml-6 my-3",
      li: "my-1",
    },
    quote: "border-l-4 border-neutral-100 pl-4 italic my-4 text-neutral-600",
    text: {
      bold: "font-semibold text-secondary-600",
      italic: "italic",
      underline: "underline",
      strikethrough: "line-through",
      code: "font-mono bg-neutral-50 px-1 py-0.5 rounded text-sm text-primary-800",
    },
    link: "text-primary-600 hover:text-primary-800 underline decoration-primary-300 cursor-pointer transition-colors",
  };

  // Configure Lexical editor
  const initialConfig = {
    namespace: `notes-editor-${props.data.episode.id}`,
    theme,
    onError: (error) => console.error(error),
    nodes: [
      HeadingNode,
      QuoteNode,
      ListItemNode,
      ListNode,
      CodeNode,
      CodeHighlightNode,
      LinkNode,
    ],
  };

  // Track saveNow prop changes
  useEffect(() => {
    if (props.saveNow) {
      handleManualSave();
    }
  }, [props.saveNow]);

  // Handle saving state changes from plugins
  const handleSaveStateChange = (saving) => {
    setIsSaving(saving);
    if (!saving) {
      setSaveStatus('saved');
      
      // Reset to ready state after showing saved confirmation
      setTimeout(() => {
        setSaveStatus('ready');
      }, 2000);
    }
  };

  // Handle manual save button click
  const handleManualSave = () => {
    if (isSaving || !editor) return; // Prevent saving if already saving or editor not ready
    
    setIsSaving(true);
    setSaveStatus('saving');
    
    try {
      // Use Lexical API directly to get current content
      const editorState = editor.getEditorState();
      let markdown = "";
      
      editorState.read(() => {
        markdown = $convertToMarkdownString(TRANSFORMERS);
      });
      
      // Call parent's onChange handler with the content
      props.onChange({
        episode_id: props.data.episode.id,
        data: markdown,
      });
      
      console.log("Editor: Manual save triggered");
      
      // Show saving status for a moment then transition to saved
      setTimeout(() => {
        setIsSaving(false);
        setSaveStatus('saved');
        
        // Reset to ready state after showing saved confirmation
        setTimeout(() => {
          setSaveStatus('ready');
        }, 2000);
      }, 500);
    } catch (error) {
      console.error("Failed to save:", error);
      setIsSaving(false);
      setSaveStatus('ready');
    }
  };

  // Capture editor reference
  const [editor, setEditor] = useState(null);
  
  return (
    <div className="lexical-editor-wrapper">
      <LexicalComposer initialConfig={initialConfig}>
        {/* Capture editor instance */}
        <EditorCapturePlugin captureEditor={setEditor} />
        
        <Toolbar 
          onSave={handleManualSave} 
          isSaving={isSaving} 
          saveStatus={saveStatus}
        />
        <div className="editor-container border border-neutral-100 border-t-0 rounded-b-md">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            placeholder={
              <div className="editor-placeholder text-neutral-500">
                Write your notes here...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin /> {/* Added LinkPlugin */}
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <RegisterCommandsPlugin />

          {/* Custom plugins for initializing content and handling saves */}
          <InitialContentPlugin initialContent={props.data.episode.note} />
          <EditorSavePlugin
            onChange={props.onChange}
            episodeId={props.data.episode.id}
            onSaveStateChange={handleSaveStateChange}
          />
          <SaveOnDemandPlugin
            saveNow={props.saveNow}
            onChange={props.onChange}
            episodeId={props.data.episode.id}
            onSaveStateChange={handleSaveStateChange}
          />
          <SaveKeyboardShortcutPlugin onSave={handleManualSave} />
        </div>
        <StatusBar status={isSaving ? 'saving' : saveStatus} />
      </LexicalComposer>
    </div>
  );
};

export default Editor;
